import { TaskState, useGetTasksListQuery } from '@/redux';
import {
  useEditTaskAction,
  useEditTaskGroupAction,
  useGroupTasksAction,
  useProjectContext,
  useTasksColumns,
  useUngroupTasksAction,
} from '@/views';
import { useDuplicateTaskAction } from '../Tasks.Details/useDuplicateTaskAction';
import {
  DefaultNumberSorting,
  Table,
  useDefaultTablePaging,
  useFilters,
  useSorting,
} from '@/components';
import { useCallback, useMemo, useState } from 'react';
import { useBulkUpdateTasksActions } from '../Tasks.Details/useBulkUpdateTasksAction';
import { TasksListActions } from './TasksListActions';
import { TaskFilterParams } from '@/core';
import { useBulkDeleteTasksAction } from '../Tasks.Details/ImportTasks/useBulkDeleteTasksAction';
import { useEditTaskCommentsAction } from './useEditTaskCommentsAction';

interface TasksManagementListPanelProps {
  projectId: string;
  communityId: string;
}

export function TasksListPanel(props: TasksManagementListPanelProps) {
  const { projectId, communityId } = props;
  const { setTotal, paging, params } = useDefaultTablePaging();
  const {
    filters,
    onFiltersChange,
    resetFilters,
    stringFilterOptions,
    setStringFilterOptions,
  } = useFilters<TaskFilterParams>(
    ['number', 'name', 'home', 'responsiblePerson', 'stepName'],
    undefined,
    ['startDate', 'endDate', 'costEstimated'],
  );
  const { sorting, onSortingChange } =
    useSorting<TaskState>(DefaultNumberSorting);
  const { data, isFetching } = useGetTasksListQuery({
    projectId,
    paging: params,
    filters,
    sorting,
  });

  const tasks = useMemo(() => {
    if (data) {
      setTotal(data.total);
      setStringFilterOptions(data.stringFilterOptions);
    }
    return data?.items;
  }, [data, setStringFilterOptions, setTotal]);

  const [selected, setSelected] = useState<TaskState[]>([]);
  const clearSelection = useCallback(() => setSelected([]), []);

  const { isActive } = useProjectContext();
  const { openEdit, editModal } = useEditTaskAction();
  const { openEdit: openEditComments, editModal: commentsModal } =
    useEditTaskCommentsAction();
  const { openDuplicate, duplicateModal } =
    useDuplicateTaskAction(clearSelection);
  const { openBulkUpdate, bulkUpdateModal } =
    useBulkUpdateTasksActions(clearSelection);
  const { openCreateGroup, groupTaskModal } =
    useGroupTasksAction(clearSelection);
  const { openEditGroup, editTaskGroupModal } = useEditTaskGroupAction();
  const ungroup = useUngroupTasksAction();
  const columns = useTasksColumns(
    openEdit,
    openDuplicate,
    openEditComments,
    ungroup,
    openEditGroup,
    tasks,
    stringFilterOptions,
  );

  const bulkDelete = useBulkDeleteTasksAction();

  const handleBulkUpdate = () =>
    openBulkUpdate(
      selected.map((x) => x.id),
      projectId,
      communityId,
    );

  const handleDuplicate = () =>
    openDuplicate(
      selected.map((x) => x.id),
      projectId,
    );

  const handleCreateGroup = () =>
    openCreateGroup(
      selected.map((x) => x.id),
      projectId,
    );

  const handleBulkDelete = () =>
    bulkDelete({ taskIds: selected.map((x) => x.id), projectId });

  const selectedRowKeys = useMemo(() => {
    const keys = new Set<string>();
    selected.forEach((task) => {
      keys.add(task.id);
      if (task.groupId) {
        keys.add(`group-${task.groupId}`);
      }
    });
    return Array.from(keys);
  }, [selected]);

  const handleSelect = (record: any, selectedFlag: boolean) => {
    const newSelected = new Map(selected.map((x) => [x.id, x]));

    if ('children' in record && Array.isArray(record.children)) {
      for (const child of record.children) {
        if (selectedFlag) {
          newSelected.set(child.id, child);
        } else {
          newSelected.delete(child.id);
        }
      }
    } else {
      if (selectedFlag) {
        newSelected.set(record.id, record);
      } else {
        newSelected.delete(record.id);
      }
    }

    setSelected(Array.from(newSelected.values()));
  };

  const handleSelectAll = (selectedFlag: boolean, selectedRows: any[]) => {
    const newSelected = new Map(selected.map((x) => [x.id, x]));

    selectedRows.forEach((row) => {
      if ('children' in row && Array.isArray(row.children)) {
        for (const child of row.children) {
          if (selectedFlag) {
            newSelected.set(child.id, child);
          } else {
            newSelected.delete(child.id);
          }
        }
      } else {
        if (selectedFlag) {
          newSelected.set(row.id, row);
        } else {
          newSelected.delete(row.id);
        }
      }
    });

    setSelected(Array.from(newSelected.values()));
  };

  const groupedTasks = useMemo(() => {
    if (!tasks) return [];

    const groupMap = new Map<string, TaskState[]>();
    const standalone: TaskState[] = [];

    for (const task of tasks) {
      if (task.groupId) {
        if (!groupMap.has(task.groupId)) {
          groupMap.set(task.groupId, []);
        }
        groupMap.get(task.groupId)!.push(task);
      } else {
        standalone.push(task);
      }
    }

    const groupedRows: TaskState[] = Array.from(groupMap.entries()).map(
      ([groupId, children]) => ({
        ...children[0],
        id: `group-${groupId}`,
        number: children[0].number,
        groupId,
        children,
      }),
    );

    return [...groupedRows, ...standalone];
  }, [tasks]);

  return (
    <div>
      <Table<TaskState>
        name="Tasks"
        scroll={{ x: 1200 }}
        actions={
          <TasksListActions
            onBulkUpdate={handleBulkUpdate}
            onDuplicate={handleDuplicate}
            onBulkDelete={handleBulkDelete}
            onCreateGroup={handleCreateGroup}
            disabled={selected.length === 0 || !isActive}
          />
        }
        expandable={{ expandIconColumnIndex: 2 }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onSelect: handleSelect,
          onSelectAll: handleSelectAll,
        }}
        loading={isFetching}
        columns={columns}
        dataSource={groupedTasks}
        rowKey={(row) => row.id}
        pagination={paging}
        onChange={(_, filters, sorting) => {
          onFiltersChange(filters);
          onSortingChange(sorting);
        }}
        onFiltersReset={resetFilters}
        defaultHiddenColumnKeys={['number', 'startDate', 'costEstimated']}
      />
      {editModal}
      {duplicateModal}
      {bulkUpdateModal}
      {commentsModal}
      {groupTaskModal}
      {editTaskGroupModal}
    </div>
  );
}
